import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, TrendingDown, Users, UserPlus, UserMinus, Activity } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Turnover = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTurnoverData();
  }, []);

  const fetchTurnoverData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dashboard/turnover`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching turnover:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="turnover-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Dashboard de Turnover
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Análise de contratações e demissões
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
              <Users size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{data.total_funcionarios}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-lg">
              <UserPlus size={24} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Ativos</p>
              <p className="text-2xl font-bold text-emerald-600">{data.ativos}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg">
              <Activity size={24} className="text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Inativos</p>
              <p className="text-2xl font-bold text-yellow-600">{data.inativos}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 dark:bg-red-900 p-3 rounded-lg">
              <UserMinus size={24} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Demitidos</p>
              <p className="text-2xl font-bold text-red-600">{data.demitidos}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-2 border-purple-500">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-lg">
              <TrendingUp size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Taxa Turnover</p>
              <p className="text-2xl font-bold text-purple-600">{data.taxa_turnover}%</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Evolution Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Evolução Mensal - Contratações vs Demissões
        </h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data.evolucao_mensal}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mes" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="contratacoes" stroke="#10B981" strokeWidth={2} name="Contratações" />
            <Line type="monotone" dataKey="demissoes" stroke="#EF4444" strokeWidth={2} name="Demissões" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Balance Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Saldo Líquido Mensal
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.evolucao_mensal}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mes" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="saldo" fill={(entry) => entry.saldo >= 0 ? '#10B981' : '#EF4444'} name="Saldo" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 dark:bg-blue-950">
        <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">
          📊 Como interpretar a Taxa de Turnover
        </h4>
        <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
          <p><strong>Até 5%:</strong> Turnover baixo - Excelente retenção de talentos</p>
          <p><strong>5% a 10%:</strong> Turnover moderado - Normal para maioria das empresas</p>
          <p><strong>10% a 15%:</strong> Turnover alto - Atenção necessária</p>
          <p><strong>Acima de 15%:</strong> Turnover crítico - Ações urgentes requeridas</p>
        </div>
      </Card>
    </div>
  );
};

export default Turnover;
